// The one place that knows how a CDN image path relates to its retina sibling. Both sides of the
// contract import from here so they can't drift apart: getImageSrc (createImageSrc.ts) decides
// which key to *request* at runtime, and findMissingRetinaKeys (below) decides which keys must
// *exist* at generation time. When those two disagree you get exactly the
// bug this module exists to prevent — either a generator demanding @2x objects nobody ever fetches,
// or a runtime fetching @2x objects nobody uploaded.
//
// Deliberately dependency-free and in src/ rather than scripts/: the script may import from src/,
// but not the reverse, or `sharp` and `@aws-sdk` would follow this into browser bundles.

/** A path that already names a retina variant explicitly (e.g. some marketing exports). */
export function isRetinaPath(path: string): boolean {
  return /@[23]x\.\w{3,4}$/.test(path);
}

export function isSvgPath(path: string): boolean {
  return /\.svg$/i.test(path);
}

/**
 * Whether a path is expected to have a real @2x object alongside it.
 *
 * True only for a base raster. An already-@2x/@3x path is its own content with no separate 1x to
 * scale up from. An SVG is resolution-independent, so a "@2x.svg" could only ever be a duplicate
 * that renders identically — requiring one means uploading a pointless copy of every SVG, and a
 * retina screen then fetches that copy instead of the original.
 */
export function expectsRetinaSibling(path: string): boolean {
  return !isRetinaPath(path) && !isSvgPath(path);
}

/**
 * The @2x key for a base path — @2x specifically, never @3x, even where a @3x object exists.
 *
 * Unguarded on purpose: it will happily turn foo@2x.png into foo@2x@2x.png. Pair it with
 * expectsRetinaSibling, which is what decides whether a path should be rewritten at all.
 */
export function toRetinaPath(path: string): string {
  return path.replace(/\.(\w{3,4})$/, "@2x.$1");
}

// getImageSrc (createImageSrc.ts) requests @2x specifically (never @3x, even if one exists) on any
// retina screen — and every screen might be retina, so every path it will ask for needs a real
// object regardless of how rarely it's actually requested. There's no per-call lookup, so a
// missing one is a broken <img> at runtime on a retina screen, not a build-time error anyone would
// otherwise notice. Catch it here instead, once, at generation time.
//
// Which paths those are is expectsRetinaSibling's call, shared with getImageSrc so the two can't
// disagree about SVGs or already-@2x literals.
export function findMissingRetinaKeys(allFiles: string[]): string[] {
  const allFileSet = new Set(allFiles);
  return allFiles.filter(
    (relativePath) => expectsRetinaSibling(relativePath) && !allFileSet.has(toRetinaPath(relativePath)),
  );
}
